import React, { lazy, Suspense, useEffect, useState } from 'react';
import { ackWatchAlert, resolveWatchAlert, getInbox } from '../../truckmateStore';
import './sidePane.css';
import { Person, consentLabel, textBlocked } from './TripCard';
import { timesModel, crewModel, healthModel, locationModel, stopsModel, documentConflicts, ocModel, NOT_REPORTED, ago, shortDate, byLabel } from './model';

const InboxPane = lazy(() => import('./PhoneMessages').then((m) => ({ default: m.InboxPane })));
const CallsPane = lazy(() => import('./PhoneMessages').then((m) => ({ default: m.CallsPane })));

// Selected-trip panel: observed facts, estimates and rule-based suggestions are
// kept visibly separate. Ownership and resolution go to the real Watchtower
// backend; nothing here sends a message to anyone.

function Fields({ rows }) {
  return (
    <dl className="tmw-fields">
      {rows.filter(Boolean).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v == null || v === '' ? NOT_REPORTED : v}</dd></div>)}
    </dl>
  );
}

function activityFor(trip, incidents, resolved) {
  const out = [];
  incidents.groups.forEach((g) => g.items.forEach((a) => {
    out.push({ t: a.openedAt, text: `Opened: ${a.title}`, by: 'Watchtower' });
    if (a.ack) out.push({ t: a.ack.at, text: `Owned: ${a.title}`, by: a.ack.by });
    if (a.lastPushAt) out.push({ t: a.lastPushAt, text: `Alert pushed (${a.pushes}×)`, by: 'Watchtower' });
  }));
  (resolved || []).filter((a) => String(a.trip) === String(trip.tripNumber)).forEach((a) => out.push({ t: a.resolvedAt, text: `Resolved: ${a.title}${a.note ? ` — “${a.note}”` : ''}`, by: a.resolvedBy === 'auto' ? 'Cleared automatically' : a.resolvedBy }));
  if (trip.rateCon && trip.rateCon.uploadedAt) out.push({ t: Date.parse(trip.rateCon.uploadedAt), text: 'Rate confirmation uploaded', by: byLabel(trip.rateCon.uploadedBy) || null });
  if (trip.manifest && trip.manifest.uploadedAt) out.push({ t: Date.parse(trip.manifest.uploadedAt), text: `Trip sheet uploaded (version ${trip.manifest.version || 1})`, by: trip.manifest.uploadedBy || null });
  Object.entries(trip.rcCheck || {}).forEach(([instr, c]) => { if (c && c.done) out.push({ t: Date.parse(c.at), text: `Signed off: ${instr}`, by: c.by }); });
  (trip.checkins || []).forEach((c) => out.push({ t: Date.parse(c.at), text: `${c.source === 'email' ? 'Carrier email' : c.source === 'driver app' ? 'Driver check-in (app)' : 'Check call'}: ${c.location ? `${c.location} — ` : ''}${c.text}`, by: c.by || c.from || null }));
  (trip.comms || []).forEach((c) => out.push({
    t: Date.parse(c.at),
    text: c.type === 'call' && c.ai ? `${c.label || 'Jarvis (AI) call'}${c.text ? `: “${String(c.text).slice(0, 120)}”` : ''}` : c.type === 'text' && (c.kind === 'breakdown' || c.kind === 'resumed') ? `Texted customer ${c.to}: ${c.kind === 'breakdown' ? 'breakdown notice' : 'back on the road'}` : c.type === 'email' ? (c.dir === 'in' ? `Email from ${c.name || c.from}: “${String(c.subject || '').slice(0, 80)}”` : `Replied by email (Jarvis) to ${c.to}`) : c.type === 'call' ? `Called ${c.label || c.to}` : c.type === 'reply' ? `Driver texted: “${String(c.text || '').slice(0, 90)}”` : `Texted the driver: ${c.kind === 'confirm-stop' ? 'confirm delivery' : c.kind === 'pod-request' ? 'send POD / BOL' : `“${String(c.text || '').replace(/^Florida Beauty Flora dispatch: /, '').replace(/ Reply STOP to opt out\.$/, '').slice(0, 90)}”`}`,
    by: c.by || c.from || null,
  }));
  Object.values(trip.stopConfirm || {}).forEach((c) => out.push({ t: Date.parse(c.at), text: `Driver confirmed ${c.stopLabel || 'a stop'} delivered`, by: 'driver text' }));
  const dl = trip.driverLink;
  if (dl) {
    if (dl.createdAt) out.push({ t: Date.parse(dl.createdAt), text: 'Driver tracking link created', by: dl.createdBy || null });
    if (dl.sentAt) out.push({ t: Date.parse(dl.sentAt), text: `Tracking link sent (${dl.sentVia || 'sent'})`, by: null });
    if (dl.openedAt) out.push({ t: Date.parse(dl.openedAt), text: `Driver opened the tracking link (${dl.openedVia === 'app' ? 'TagAlong app' : 'browser'})`, by: null });
    if (dl.stoppedAt) out.push({ t: Date.parse(dl.stoppedAt), text: 'Driver stopped sharing location', by: null });
  }
  Object.values(trip.visits || {}).forEach((v) => (v.evidence || []).forEach((e) => out.push({ t: Date.parse(e.at), text: `${v.geofence ? v.geofence.name : 'Stop'}: ${e.event} — ${e.detail}`, by: 'Geofence' })));
  return out.filter((x) => x.t && !Number.isNaN(x.t)).sort((a, b) => b.t - a.t).slice(0, 14);
}

// the side pane's two views: the selected truck, or the shared text inbox (fast access while working the board)
const SIDE_KEY = 'tmw-side';
const readSide = () => { try { const v = localStorage.getItem(SIDE_KEY); return v === 'messages' || v === 'calls' ? v : 'truck'; } catch { return 'truck'; } };
function SideTabs({ side, setSide, unread }) {
  return (
    <div className="tmw-side-tabs" role="tablist" aria-label="Side pane">
      <button type="button" role="tab" aria-selected={side === 'truck'} onClick={() => setSide('truck')}>Truck</button>
      <button type="button" role="tab" aria-selected={side === 'messages'} onClick={() => setSide('messages')}>Messages{unread > 0 && <span className="pm-unread" aria-label={`${unread} unread`}>{unread}</span>}</button>
      <button type="button" role="tab" aria-selected={side === 'calls'} onClick={() => setSide('calls')}>Calls</button>
    </div>
  );
}

export default function DetailPanel(props) {
  const { trip } = props;
  const [side, setSideState] = useState(readSide);
  const [unread, setUnread] = useState(0);
  const setSide = (v) => { setSideState(v); try { localStorage.setItem(SIDE_KEY, v); } catch { /* private window */ } };
  // the unread badge while the truck view is up
  useEffect(() => {
    if (side === 'messages' || typeof getInbox !== 'function') return undefined;
    let off = false;
    const tick = () => Promise.resolve().then(() => getInbox()).then((r) => { if (!off && r) setUnread((r.threads || []).reduce((n, t) => n + (t.unread || 0), 0)); }).catch(() => {});
    tick(); const t = setInterval(tick, 30000);
    return () => { off = true; clearInterval(t); };
  }, [side]);
  if (props.brief) return <TruckDetail {...props} />;
  const tabs = <SideTabs side={side} setSide={setSide} unread={unread} />;
  if (side === 'calls') {
    return (
      <aside className="tmw-detail" aria-label="Calls">
        {tabs}
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><CallsPane trip={trip || null} drivers={trip ? crewModel(trip).drivers : []} /></Suspense>
      </aside>
    );
  }
  if (side === 'messages') {
    return (
      <aside className="tmw-detail" aria-label="Messages">
        {tabs}
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><InboxPane trip={trip || null} drivers={trip ? crewModel(trip).drivers : []} onUnread={setUnread} /></Suspense>
      </aside>
    );
  }
  return <TruckDetail {...props} tabs={tabs} />;
}

function TruckDetail({ trip, glance, incidents, resolved, brief, onCloseBrief, onChanged, onJump, onClose, cardVisible, tabs = null }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  if (brief) {
    return (
      <aside className="tmw-detail" aria-label="Shift brief" aria-live="polite">
        <div className="tmw-detail-head"><span>SHIFT BRIEF · from open Watchtower issues</span><button type="button" className="tmw-link" onClick={onCloseBrief}>Back</button></div>
        <h2>For the next shift</h2>
        {brief.error ? <div className="tmw-conflict">{brief.error}</div> : <pre className="tmw-pre">{brief.text || 'Loading…'}</pre>}
        <div className="tmw-actions">
          <button type="button" className="tmw-btn tmw-btn-primary" disabled={!brief.text} onClick={() => { try { navigator.clipboard.writeText(brief.text); } catch { /* ignore */ } }}>Copy</button>
        </div>
        <div className="tmw-source">Draft text only — nothing is sent or shared.</div>
      </aside>
    );
  }
  if (!trip) {
    return (
      <aside className="tmw-detail" aria-label="Selected truck">
        {tabs}
        <div className="tmw-panel-title">No truck selected</div>
        <p className="tmw-note">Choose <b>Details</b> on a truck to see its issues, estimates and activity here. The panel only changes when you choose a truck.</p>
      </aside>
    );
  }
  const crew = crewModel(trip);
  const oc = ocModel(trip);
  const loc = locationModel(trip);
  const stops = stopsModel(trip);
  const conflicts = [...crew.conflicts, ...((trip.manifest && trip.manifest.diffs) || []).map((d) => d.msg), ...documentConflicts(trip).map((c) => c.msg), ...(trip.alerts || []).map((a) => a.msg)];
  const top = incidents.groups[0];
  const faults = oc ? 0 : healthModel(trip).faults.length;   // same count the card shows under Needs attention
  const wheel = crew.drivers.find((d) => crew.atWheel.verified && d.name === crew.atWheel.name) || crew.drivers[0];
  const act = async (id, fn) => {
    setBusy(id); setErr('');
    try { await fn(); if (onChanged) onChanged(); } catch (e) { setErr(e.message || 'Could not update.'); } finally { setBusy(''); }
  };
  const history = activityFor(trip, incidents, resolved);
  return (
    <aside className="tmw-detail" aria-label={`Details for trip ${trip.tripNumber}`} aria-live="polite">
      {tabs}
      <div className="tmw-panel-title">
        {oc
          ? <span>Selected: <b>OC · {oc.carrierName}</b> · Trip <b className="numeric">{trip.tripNumber}</b></span>
          : <span>Selected truck: <b className="numeric">{trip.powerUnit || '—'}</b> · Trip <b className="numeric">{trip.tripNumber}</b></span>}
        <button type="button" className="tmw-close" aria-label="Close selected truck details" onClick={onClose}>✕</button>
      </div>
      {!cardVisible && (
        <div className="tmw-offscreen" role="status">
          <span>Viewing details for {oc ? `OC · ${oc.carrierName}` : `Truck ${trip.powerUnit || '—'}`} (trip {trip.tripNumber}) — its card is not on screen.</span>
          <button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" onClick={onJump}>Jump to truck</button>
        </div>
      )}
      <h2>{top ? top.title : faults ? `${faults} engine fault${faults === 1 ? '' : 's'}` : 'No open alerts'}</h2>
      <div className="tmw-note">{oc ? `Outside carrier · ${oc.carrierName}` : `${crew.drivers.map((d) => d.name).join(' & ') || 'Driver not provided'} · ${crew.mode}`}</div>

      <div className="tmw-rule" />
      <div className="tmw-kicker">Observed</div>
      <Fields rows={[
        ...(timesModel(trip) ? [
          ['Trip created', timesModel(trip).created],
          ['Added to AI Dispatcher', timesModel(trip).added],
          ['Dispatched', timesModel(trip).dispatched],
        ] : []),
        ['Trip trailer', trip.trailer],
        ...(oc && !loc.gps ? [['GPS', 'None — outside carrier (see last check-in)']] : [
          [oc ? 'Current location' : 'GPS location', loc.gps ? `${loc.gps}` : null],
          ['GPS updated', loc.gps ? `${loc.gpsAge || '—'} · ${loc.source || 'GPS'}${loc.gpsStale ? ' · stale' : ''}` : null],
        ]),
        ['TruckMate zone', loc.truckmateZone],
        oc && loc.speedMph == null ? null : ['Speed', loc.speedMph != null ? `${Math.round(loc.speedMph)} mph` : null],
        ...(oc ? [
          ['Outside carrier', oc.carrierName],
          ['Crew', oc.crew === 'team' ? 'Team — 2 drivers' : 'Solo — 1 driver'],
          ['Their truck', oc.truck],
          [oc.crew === 'team' ? 'Driver 1' : 'Driver', <Person name={oc.driverName} phone={oc.driverPhone} trip={trip.tripNumber} />],
          oc.crew === 'team' ? ['Driver 2', <Person name={oc.driver2Name} phone={oc.driver2Phone} trip={trip.tripNumber} which={2} />] : null,
          ['Texts', oc.smsConsent ? `Driver agreed · ${oc.smsConsent.by}` : 'No consent recorded yet'],
          oc.missing.length ? ['Missing driver info', oc.missing.join(', ')] : null,
          ['Carrier main phone', oc.dispatchPhone],
          ['Last check-in', `${oc.lastLabel}${oc.last && !/Driver app/.test(oc.lastLabel) ? ` — ${oc.last.location || oc.last.text}` : ''}`],
          ['Tracking link', oc.link ? oc.link.label : 'Not sent — create one in Outside carrier'],
        ] : [
          ['At the wheel', crew.atWheel.verified ? crew.atWheel.name : crew.atWheel.note],
          ...crew.drivers.map((d) => [d.role, <Person name={d.name} phone={d.phone} trip={trip.tripNumber} which={d.which} callOnly={d.phoneSource === 'Trip sheet'} blocked={textBlocked(d)} />]),
          ...crew.drivers.filter((d) => d.phone).map((d) => [`${d.role} texts`, consentLabel(d)]),
          ['Drive left', wheel ? `${wheel.driveLeft}${wheel.stale && wheel.statusKey ? ' (stale)' : ''}` : null],
        ]),
        trip.live && trip.live.tempF != null ? ['Reefer temperature', `${trip.live.tempF}°F${trip.live.tempStale ? ' (stale)' : ''}`] : null,
        ['Next stop', stops.current ? `At ${stops.current.customer || stops.current.place}` : (glance && glance.nextStopName) || (stops.next ? stops.next.customer || stops.next.place : null)],
      ]} />

      <div className="tmw-rule" />
      <div className="tmw-kicker">Estimates</div>
      <Fields rows={[
        ['Remaining (approx.)', glance.remainingMi != null ? `~${glance.remainingMi.toLocaleString()} mi` : null],
        [glance.etaSource === 'server' ? 'ETA (driving hours + road miles — same as alerts)' : 'ETA (rough estimate)', glance.hubHold ? `Still at the ${glance.hubHold} hub — not departed yet` : glance.eta ? `${shortDate(glance.eta)}${glance.remainingMi ? ` · ${glance.remainingMi} mi` : ''}` : null],
        ['Appointment', glance.appointment],
      ]} />
      <div className="tmw-source">Distance is straight-line ×1.2 to the next stop; ETA assumes 60 mph, DOT driving hours (solo or team) and 1 hour per stop. Both are estimates.</div>

      {conflicts.length > 0 && (
        <>
          <div className="tmw-rule" />
          <div className="tmw-kicker">Source conflicts</div>
          {conflicts.map((c, i) => <div className="tmw-conflict" key={i}>{c}</div>)}
          <div className="tmw-source">Resolve conflicts before relying on decisions that depend on them.</div>
        </>
      )}

      <div className="tmw-rule" />
      <div className="tmw-kicker">Open issues {incidents.count ? `(${incidents.count})` : ''}</div>
      {err && <div className="tmw-conflict">{err}</div>}
      {!incidents.groups.length && <div className="tmw-note">Watchtower has nothing open for this trip.</div>}
      {incidents.groups.map((g) => (
        <div className="tmw-incident" data-sev={g.severity} key={g.code}>
          <strong>{g.severity === 'critical' ? 'Critical · ' : 'Warning · '}{g.title}</strong>
          <div><span className="tmw-label">Suggested next step (rule-based)</span>{g.detail}</div>
          {g.items.length > 1 && <div className="tmw-small">{g.items.length} related alerts grouped</div>}
          {g.items.map((a) => (
            <div className="tmw-actions" key={a.id}>
              {a.ack ? <span className="tmw-small">Owned by {a.ack.by} · {ago(a.ack.at)}</span>
                : <button type="button" className="tmw-btn tmw-btn-primary tmw-btn-sm" disabled={busy === a.id} onClick={() => act(a.id, () => ackWatchAlert(a.id))}>Take ownership</button>}
              <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy === a.id}
                onClick={() => { const note = window.prompt('What was done? (saved as the resolution)', ''); if (note && note.trim()) act(a.id, () => resolveWatchAlert(a.id, note.trim())); }}>Resolve…</button>
            </div>
          ))}
        </div>
      ))}
      {incidents.alsoAffects.length > 0 && <div className="tmw-note">Same truck also has open issues on trip{incidents.alsoAffects.length === 1 ? '' : 's'} {incidents.alsoAffects.join(', ')}.</div>}

      <div className="tmw-rule" />
      <div className="tmw-kicker">Activity</div>
      {history.length ? (
        <div className="tmw-history">
          {history.map((h, i) => <p key={i}>{h.text}<time>{shortDate(h.t)}{h.by ? ` · ${h.by}` : ''}</time></p>)}
        </div>
      ) : <div className="tmw-note">No recorded activity yet.</div>}
    </aside>
  );
}
